import { isAfter, subDays } from "date-fns";
import { FC, useEffect, useMemo, useState } from "react";

import { AttendanceCard } from "./AttendanceCard";
import { EngagementCard } from "./EngagementCard";
import { EngagementTable } from "./EngagementTable";
import { SessionLogTable } from "./SessionLogTable";
import { SessionsCard } from "./SessionsCard";
import { StudentTalkTimeCard } from "./StudentTalkTimeCard";

import { EngagementLevel, UserEngagementDataType } from "api/api-data-types";
import { serverGetEngagementData } from "api/api-server-data";
import { ErrorScreen, LoadingScreen } from "components/LoadingScreen";
import { ALL_TIME_DATA } from "components/constants";
import { t } from "i18n/i18n";
import { SortableTableDateRange, SortableTableDateRangeOption } from "mds/components/SortableTable";
import { useCapabilities, useUserIdsWhoCanParticipateInCourse } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectAllVisibleClassSessionsForCurrentCourse,
  selectCurrentCourseId,
} from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";

const isVirtualClassSession = (session: { category: string | null }) => {
  return session.category === "virtual";
};

export const SessionLogView: FC = () => {
  const currentCourseId = useAppSelector(selectCurrentCourseId);
  const { canAuthorCourse } = useCapabilities();

  useEffect(() => {
    if (currentCourseId) {
      storeApi.class_session_summaries.list({ section_id: currentCourseId });
      storeApi.class_session_user_summaries.list({ section_id: currentCourseId });
    }
  }, [currentCourseId]);
  const [selectedDateRange, setSelectedDateRange] = useState<SortableTableDateRangeOption | null>(
    null,
  );
  const allVisibleClassSessions = useAppSelector(selectAllVisibleClassSessionsForCurrentCourse);

  const {
    data: engagementData,
    isLoading: isLoadingEngagementData,
    error: errorEngagementData,
  } = usePromiseEffect<Record<number, Record<string, UserEngagementDataType>> | null>(async () => {
    if (!currentCourseId) {
      return null;
    }
    return serverGetEngagementData(currentCourseId);
  }, [currentCourseId]);

  // Filter sessions by date range
  const filteredSessions = useMemo(() => {
    if (!selectedDateRange || selectedDateRange.value === ALL_TIME_DATA) {
      return allVisibleClassSessions;
    }
    const filterDate = subDays(new Date(), selectedDateRange.value);
    return allVisibleClassSessions.filter((cs) => {
      if (!cs.started_at) return false;
      return isAfter(new Date(cs.started_at), filterDate);
    });
  }, [allVisibleClassSessions, selectedDateRange]);

  // Filter engagement data by selected date range.
  const filteredEngagementData = useMemo(() => {
    if (!engagementData) return null;
    const selectedRange = selectedDateRange?.value ?? ALL_TIME_DATA;
    return engagementData[selectedRange] ?? null;
  }, [engagementData, selectedDateRange]);

  // Filter out non-student users from engagement data.
  const studentUserIds = useUserIdsWhoCanParticipateInCourse();
  const studentEngagementData = useMemo(() => {
    if (!filteredEngagementData) return null;
    return Object.fromEntries(
      Object.entries(filteredEngagementData).filter(([userId]) => studentUserIds.includes(userId)),
    );
  }, [filteredEngagementData, studentUserIds]);

  // To avoid cards disappearing when the date range is changed, let's use all class sessions to compute hasVirtualSessions and hasEngagementData.
  const hasVirtualSessions = useMemo(() => {
    return allVisibleClassSessions.some((session) => isVirtualClassSession(session));
  }, [allVisibleClassSessions]);

  const hasEngagementData = useMemo(() => {
    if (!engagementData) return false;
    const allTimeEngagementData = engagementData[ALL_TIME_DATA] ?? {};
    const userEntries = Object.entries(allTimeEngagementData);
    if (userEntries.length === 0) return false;
    return userEntries.some(
      ([userId, data]) =>
        studentUserIds.includes(userId) &&
        (data.effort_level !== EngagementLevel.NONE ||
          data.performance_level !== EngagementLevel.NONE),
    );
  }, [engagementData, studentUserIds]);

  if (isLoadingEngagementData) {
    return <LoadingScreen>{t("common.loading")}...</LoadingScreen>;
  }
  if (errorEngagementData) {
    return <ErrorScreen>Error loading session logs</ErrorScreen>;
  }

  return (
    <main className="mt-4">
      <div className="mb-8 flex w-full items-center justify-between rounded-lg bg-black-tint-97 px-4 py-2">
        <div className="flex items-center gap-3">
          <p className="text-sm font-medium text-black-tint-20">Showing data from:</p>
          <SortableTableDateRange value={selectedDateRange} onChange={setSelectedDateRange} />
        </div>
        <p className="text-sm font-medium text-black-tint-55">
          {t("session_log_view.applies_to_all_metrics")}
        </p>
      </div>
      <div className="flex flex-col gap-10">
        {canAuthorCourse && (
          <div className="flex w-full gap-4">
            <div className="flex-1">
              <SessionsCard sessions={filteredSessions} />
            </div>
            <div className="flex-1">
              <AttendanceCard sessions={filteredSessions} />
            </div>
            {hasEngagementData && (
              <div className="flex-1">
                <EngagementCard engagementData={studentEngagementData} />
              </div>
            )}
            {hasVirtualSessions && (
              <div className="flex-1">
                <StudentTalkTimeCard sessions={filteredSessions} />
              </div>
            )}
          </div>
        )}
        <SessionLogTable sessions={filteredSessions} />
        {canAuthorCourse && (
          <EngagementTable engagementData={studentEngagementData} sessions={filteredSessions} />
        )}
      </div>
    </main>
  );
};
